import { type FC, Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";
import type { MetaAdsCampaignApprovalListItem } from "shared";
import { MetaAdsCampaignApprovalStatus } from "shared";
import { Card } from "../components/Card";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { formatMetaAdsMoney } from "./metaAdsMoney";
import { useApprovals } from "./useApprovals";

const PAGE_SIZE = 10;

const STATUS_BADGE: Record<string, string> = {
	pending: "bg-warning text-dark",
	approved: "bg-success",
	rejected: "bg-danger",
};

const TABS = [
	{ label: "All", value: undefined },
	{ label: "Pending", value: MetaAdsCampaignApprovalStatus.PENDING },
	{ label: "Approved", value: MetaAdsCampaignApprovalStatus.APPROVED },
	{ label: "Rejected", value: MetaAdsCampaignApprovalStatus.REJECTED },
] as const;

const ApprovalsTable: FC<{ status?: string }> = ({ status }) => {
	const { data: approvals } = useApprovals(status);
	const navigate = useNavigate();
	const [page, setPage] = useState(0);

	if (approvals.length === 0) {
		return (
			<Card>
				<Card.Body>
					<p className="text-muted" style={{ marginBottom: 0 }}>
						No approval requests found.
					</p>
				</Card.Body>
			</Card>
		);
	}

	const totalPages = Math.max(1, Math.ceil(approvals.length / PAGE_SIZE));
	const safePage = Math.min(page, totalPages - 1);
	const pageApprovals = approvals.slice(
		safePage * PAGE_SIZE,
		(safePage + 1) * PAGE_SIZE,
	);

	return (
		<Card>
			<Card.Body style={{ padding: 0, overflowX: "auto" }}>
				<table className="table" style={{ marginBottom: 0 }}>
					<thead>
						<tr>
							<th>Campaign</th>
							<th>Objective</th>
							<th>Budget</th>
							<th>Submitted By</th>
							<th>Date</th>
							<th>Status</th>
						</tr>
					</thead>
					<tbody>
						{pageApprovals.map((item: MetaAdsCampaignApprovalListItem) => (
							<tr
								key={item.id}
								style={{ cursor: "pointer" }}
								onClick={() =>
									navigate(LinkGen.MetaAds.ApprovalReview(item.id))
								}
							>
								<td>
									<strong>{item.campaignName || "(unnamed)"}</strong>
								</td>
								<td>{item.objective}</td>
								<td>
									{item.budgetType} - {formatMetaAdsMoney(item.budgetAmount)}
								</td>
								<td>{item.submittedByName}</td>
								<td>{new Date(item.submittedAt).toLocaleDateString()}</td>
								<td>
									<span
										className={`badge ${STATUS_BADGE[item.status] ?? "bg-secondary"}`}
									>
										{item.status}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
				{totalPages > 1 && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							gap: 8,
							padding: 12,
						}}
					>
						<button
							type="button"
							className="btn btn-sm btn-outline-secondary"
							disabled={safePage === 0}
							onClick={() => setPage((p) => Math.max(0, p - 1))}
						>
							Previous
						</button>
						<span style={{ alignSelf: "center" }}>
							Page {safePage + 1} of {totalPages}
							<span className="text-muted">
								{" "}
								· {approvals.length} approvals
							</span>
						</span>
						<button
							type="button"
							className="btn btn-sm btn-outline-secondary"
							disabled={safePage >= totalPages - 1}
							onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
						>
							Next
						</button>
					</div>
				)}
			</Card.Body>
		</Card>
	);
};

export const PageMetaAdsApprovals: FC = () => {
	const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

	return (
		<AppPage title="Campaign Approvals">
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 16,
				}}
			>
				<ul className="nav nav-tabs">
					{TABS.map((tab) => (
						<li key={tab.label} className="nav-item">
							<button
								type="button"
								className={`nav-link${activeTab === tab.value ? " active" : ""}`}
								onClick={() => setActiveTab(tab.value)}
							>
								{tab.label}
							</button>
						</li>
					))}
				</ul>

				<ErrorBoundary
					fallback={
						<Card>
							<Card.Body>
								<p className="text-muted" style={{ marginBottom: 0 }}>
									Unable to load approvals.
								</p>
							</Card.Body>
						</Card>
					}
				>
					<Suspense fallback={<p>Loading approvals...</p>}>
						<ApprovalsTable key={activeTab ?? "all"} status={activeTab} />
					</Suspense>
				</ErrorBoundary>
			</div>
		</AppPage>
	);
};
